/**
 * PetOwnerDashboard - Dashboard for pet owners to manage their pets
 * Features: Pet profiles, vaccination tracking, appointments, reminders
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { 
  Dog, Cat, Bird, Fish, Plus, Calendar, Syringe, Bell, Clock,
  CheckCircle, AlertCircle, Trash2, Edit, ChevronRight, 
  Heart, PawPrint, Loader2, CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pet type icons
const PET_ICONS = {
  dog: Dog,
  cat: Cat,
  bird: Bird,
  fish: Fish,
  other: PawPrint
};

// Pet type colors
const PET_COLORS = {
  dog: 'from-amber-500 to-orange-500',
  cat: 'from-purple-500 to-pink-500',
  bird: 'from-sky-500 to-blue-500',
  fish: 'from-teal-500 to-cyan-500',
  other: 'from-gray-500 to-slate-500'
};

// Reminder type icons & colors
const REMINDER_TYPES = {
  vaccination: { icon: Syringe, color: 'text-green-600 bg-green-100' },
  grooming: { icon: PawPrint, color: 'text-pink-600 bg-pink-100' },
  checkup: { icon: Heart, color: 'text-red-600 bg-red-100' },
  medication: { icon: Plus, color: 'text-blue-600 bg-blue-100' },
  other: { icon: Bell, color: 'text-gray-600 bg-gray-100' }
};

// Summary Card Component
function SummaryCard({ icon: Icon, title, value, subtitle, color }) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className={`h-2 bg-gradient-to-r ${color}`} />
        <div className="p-4 flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${color} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-gray-500">{title}</p>
            {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Pet Card Component
function PetCard({ pet, onEdit, onDelete, onViewDetails }) {
  const Icon = PET_ICONS[pet.type] || PawPrint;
  const gradient = PET_COLORS[pet.type] || PET_COLORS.other;
  
  // Calculate age
  const getAge = () => {
    if (!pet.date_of_birth) return null;
    const birth = new Date(pet.date_of_birth);
    const now = new Date();
    const years = Math.floor((now - birth) / (365.25 * 24 * 60 * 60 * 1000));
    const months = Math.floor(((now - birth) % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000));
    if (years > 0) return `${years}y ${months}m`;
    return `${months}m`;
  };
  
  return (
    <Card className="group hover:shadow-lg transition-all cursor-pointer overflow-hidden" data-testid={`pet-card-${pet.pet_id}`}>
      <div className={`h-24 bg-gradient-to-r ${gradient} relative`}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '15px 15px'
          }} />
        </div>
        <div className="absolute -bottom-8 left-4 w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center">
          {pet.photo_url ? (
            <img src={pet.photo_url} alt={pet.name} className="w-full h-full object-cover rounded-2xl" />
          ) : (
            <Icon className="w-8 h-8 text-gray-700" />
          )}
        </div>
        <div className="absolute top-2 right-2 flex gap-1">
          <button 
            onClick={(e) => { e.stopPropagation(); onEdit(pet); }}
            className="p-1.5 bg-white/20 hover:bg-white/40 rounded-full transition-colors"
          >
            <Edit className="w-4 h-4 text-white" />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onDelete(pet.pet_id); }}
            className="p-1.5 bg-white/20 hover:bg-red-500/80 rounded-full transition-colors"
          >
            <Trash2 className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
      
      <CardContent className="pt-10 pb-4" onClick={() => onViewDetails(pet)}>
        <h3 className="font-bold text-lg">{pet.name}</h3>
        <p className="text-sm text-gray-500 capitalize">{pet.breed || pet.type}</p>
        
        <div className="flex flex-wrap gap-2 mt-3">
          {getAge() && (
            <Badge variant="secondary" className="text-xs">
              <Calendar className="w-3 h-3 mr-1" />
              {getAge()}
            </Badge>
          )}
          {pet.weight_kg && (
            <Badge variant="secondary" className="text-xs">
              {pet.weight_kg}kg
            </Badge>
          )}
          {pet.microchip_id && (
            <Badge variant="outline" className="text-xs text-green-600 border-green-200">
              <CheckCircle className="w-3 h-3 mr-1" />
              Chipped
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Reminder Item Component
function ReminderItem({ reminder, onComplete, onDelete }) {
  const config = REMINDER_TYPES[reminder.reminder_type] || REMINDER_TYPES.other;
  const Icon = config.icon;
  const isOverdue = reminder.is_overdue;
  
  return (
    <div className={`flex items-center gap-4 p-3 rounded-lg border ${isOverdue ? 'border-red-200 bg-red-50' : 'border-gray-100'}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <p className="font-medium">{reminder.title}</p>
          {isOverdue && <Badge variant="destructive" className="text-xs">Overdue</Badge>}
        </div>
        <p className="text-sm text-gray-500">
          {reminder.pet_name} • Due: {new Date(reminder.due_date).toLocaleDateString()}
        </p>
      </div>
      <div className="flex gap-1">
        <Button 
          size="sm" 
          variant="ghost"
          onClick={() => onComplete(reminder.reminder_id)}
        >
          <CheckCircle className="w-4 h-4 text-green-600" />
        </Button>
        <Button 
          size="sm" 
          variant="ghost"
          onClick={() => onDelete(reminder.reminder_id)}
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}

// Appointment Item Component
function AppointmentItem({ appointment }) {
  const navigate = useNavigate();
  
  return (
    <Card className="hover:shadow-md transition-all cursor-pointer" onClick={() => navigate(`/bookings/${appointment.booking_id}`)}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className="w-14 h-14 bg-indigo-100 rounded-xl flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-indigo-600">
            {new Date(appointment.appointment.date).getDate()}
          </span>
          <span className="text-xs text-indigo-500">
            {new Date(appointment.appointment.date).toLocaleDateString('en-US', { month: 'short' })}
          </span>
        </div>
        <div className="flex-1">
          <h4 className="font-semibold">{appointment.service_name}</h4>
          <p className="text-sm text-gray-500">{appointment.provider_name}</p>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={appointment.status === 'CONFIRMED' ? 'default' : 'secondary'}>
              {appointment.status}
            </Badge>
            <span className="text-xs text-gray-400">
              <Clock className="w-3 h-3 inline mr-1" />
              {appointment.appointment.time}
            </span>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </CardContent>
    </Card>
  );
}

// Add Pet Dialog
function AddPetDialog({ open, onOpenChange, onSave, editPet = null }) {
  const [formData, setFormData] = useState({
    name: '',
    type: 'dog',
    breed: '',
    date_of_birth: '',
    weight_kg: '',
    size: '',
    color: '',
    microchip_id: '',
    notes: ''
  });
  
  useEffect(() => {
    if (editPet) {
      setFormData({
        name: editPet.name || '',
        type: editPet.type || 'dog',
        breed: editPet.breed || '',
        date_of_birth: editPet.date_of_birth || '',
        weight_kg: editPet.weight_kg || '',
        size: editPet.size || '',
        color: editPet.color || '',
        microchip_id: editPet.microchip_id || '',
        notes: editPet.notes || ''
      });
    } else {
      setFormData({
        name: '',
        type: 'dog',
        breed: '',
        date_of_birth: '',
        weight_kg: '',
        size: '',
        color: '',
        microchip_id: '',
        notes: ''
      });
    }
  }, [editPet, open]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, editPet?.pet_id);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editPet ? 'Edit Pet' : 'Add New Pet'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Name *</Label>
              <Input 
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Pet's name"
              />
            </div>
            <div>
              <Label>Type *</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dog">Dog</SelectItem>
                  <SelectItem value="cat">Cat</SelectItem>
                  <SelectItem value="bird">Bird</SelectItem>
                  <SelectItem value="fish">Fish</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Breed</Label>
              <Input 
                value={formData.breed}
                onChange={(e) => setFormData({...formData, breed: e.target.value})}
                placeholder="e.g. Golden Retriever"
              />
            </div>
            <div>
              <Label>Date of Birth</Label>
              <Input 
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Weight (kg)</Label>
              <Input 
                type="number"
                step="0.1"
                value={formData.weight_kg}
                onChange={(e) => setFormData({...formData, weight_kg: parseFloat(e.target.value) || ''})}
                placeholder="kg"
              />
            </div>
            <div>
              <Label>Size</Label>
              <Select value={formData.size} onValueChange={(v) => setFormData({...formData, size: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                  <SelectItem value="xl">Extra Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Color</Label>
              <Input 
                value={formData.color}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
                placeholder="e.g. Golden"
              />
            </div>
          </div>
          
          <div>
            <Label>Microchip ID</Label>
            <Input 
              value={formData.microchip_id}
              onChange={(e) => setFormData({...formData, microchip_id: e.target.value})}
              placeholder="15-digit microchip number"
            />
          </div>
          
          <div>
            <Label>Notes</Label>
            <Textarea 
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Any special needs, allergies, or notes..."
              rows={2}
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {editPet ? 'Save Changes' : 'Add Pet'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Add Reminder Dialog
function AddReminderDialog({ open, onOpenChange, onSave, pets }) {
  const [formData, setFormData] = useState({
    pet_id: '',
    reminder_type: 'vaccination',
    title: '',
    due_date: '',
    notes: ''
  });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    setFormData({
      pet_id: '',
      reminder_type: 'vaccination',
      title: '',
      due_date: '',
      notes: ''
    });
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Reminder</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Pet *</Label>
            <Select value={formData.pet_id} onValueChange={(v) => setFormData({...formData, pet_id: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Select pet" />
              </SelectTrigger>
              <SelectContent>
                {pets.map(pet => (
                  <SelectItem key={pet.pet_id} value={pet.pet_id}>
                    {pet.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type *</Label>
              <Select value={formData.reminder_type} onValueChange={(v) => setFormData({...formData, reminder_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vaccination">Vaccination</SelectItem>
                  <SelectItem value="grooming">Grooming</SelectItem>
                  <SelectItem value="checkup">Health Checkup</SelectItem>
                  <SelectItem value="medication">Medication</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date *</Label>
              <Input 
                type="date"
                required
                value={formData.due_date}
                onChange={(e) => setFormData({...formData, due_date: e.target.value})}
              />
            </div>
          </div>
          
          <div>
            <Label>Title *</Label>
            <Input 
              required
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="e.g. Annual vaccination due"
            />
          </div>
          
          <div>
            <Label>Notes</Label>
            <Textarea 
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Any additional details..."
              rows={2}
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!formData.pet_id}>
              Add Reminder
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Main Dashboard Component
export default function PetOwnerDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [pets, setPets] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [appointments, setAppointments] = useState([]);
  
  // Dialog states
  const [addPetOpen, setAddPetOpen] = useState(false);
  const [editPet, setEditPet] = useState(null);
  const [addReminderOpen, setAddReminderOpen] = useState(false);
  
  // Get user email from localStorage (in production, use auth context)
  useEffect(() => {
    const email = localStorage.getItem('userEmail') || 'test@example.com';
    setUserEmail(email);
  }, []);
  
  // Fetch all data
  useEffect(() => {
    if (!userEmail) return;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const [summaryRes, petsRes, remindersRes, appointmentsRes] = await Promise.all([
          fetch(`${API_URL}/api/pets/dashboard/summary?user_email=${encodeURIComponent(userEmail)}`),
          fetch(`${API_URL}/api/pets/dashboard/pets?user_email=${encodeURIComponent(userEmail)}`),
          fetch(`${API_URL}/api/pets/dashboard/reminders?user_email=${encodeURIComponent(userEmail)}`),
          fetch(`${API_URL}/api/pets/dashboard/appointments?user_email=${encodeURIComponent(userEmail)}`)
        ]);
        
        const summaryData = await summaryRes.json();
        const petsData = await petsRes.json();
        const remindersData = await remindersRes.json();
        const appointmentsData = await appointmentsRes.json();
        
        if (summaryData.success) setSummary(summaryData.summary);
        if (petsData.success) setPets(petsData.pets);
        if (remindersData.success) setReminders(remindersData.reminders);
        if (appointmentsData.success) setAppointments(appointmentsData.appointments);
        
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [userEmail]);
  
  // Add/Edit pet
  const handleSavePet = async (data, petId = null) => {
    try {
      const url = petId 
        ? `${API_URL}/api/pets/dashboard/pets/${petId}`
        : `${API_URL}/api/pets/dashboard/pets?user_email=${encodeURIComponent(userEmail)}`;
      
      const response = await fetch(url, {
        method: petId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(petId ? 'Pet updated!' : 'Pet added!');
        setAddPetOpen(false);
        setEditPet(null);
        // Refresh data
        const petsRes = await fetch(`${API_URL}/api/pets/dashboard/pets?user_email=${encodeURIComponent(userEmail)}`);
        const petsData = await petsRes.json();
        if (petsData.success) setPets(petsData.pets);
      } else {
        toast.error(result.message || 'Failed to save pet');
      }
    } catch (error) {
      console.error('Error saving pet:', error);
      toast.error('Failed to save pet');
    }
  };
  
  // Delete pet
  const handleDeletePet = async (petId) => {
    if (!window.confirm('Are you sure you want to delete this pet?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/pets/dashboard/pets/${petId}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Pet deleted');
        setPets(pets.filter(p => p.pet_id !== petId));
      } else {
        toast.error(result.message || 'Failed to delete pet');
      }
    } catch (error) {
      console.error('Error deleting pet:', error);
      toast.error('Failed to delete pet');
    }
  };
  
  // Add reminder
  const handleAddReminder = async (data) => {
    try {
      const response = await fetch(`${API_URL}/api/pets/dashboard/reminders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Reminder added!');
        setAddReminderOpen(false);
        // Refresh reminders
        const remindersRes = await fetch(`${API_URL}/api/pets/dashboard/reminders?user_email=${encodeURIComponent(userEmail)}`);
        const remindersData = await remindersRes.json();
        if (remindersData.success) setReminders(remindersData.reminders);
      } else {
        toast.error(result.message || 'Failed to add reminder');
      }
    } catch (error) {
      console.error('Error adding reminder:', error);
      toast.error('Failed to add reminder');
    }
  };
  
  // Complete reminder
  const handleCompleteReminder = async (reminderId) => {
    try {
      const response = await fetch(`${API_URL}/api/pets/dashboard/reminders/${reminderId}/complete`, {
        method: 'PUT'
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Reminder completed!');
        setReminders(reminders.filter(r => r.reminder_id !== reminderId));
      }
    } catch (error) {
      console.error('Error completing reminder:', error);
    }
  };
  
  // Delete reminder
  const handleDeleteReminder = async (reminderId) => {
    try {
      const response = await fetch(`${API_URL}/api/pets/dashboard/reminders/${reminderId}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Reminder deleted');
        setReminders(reminders.filter(r => r.reminder_id !== reminderId));
      }
    } catch (error) {
      console.error('Error deleting reminder:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <PawPrint className="w-10 h-10" />
            <div>
              <h1 className="text-3xl font-bold">My Pets Dashboard</h1>
              <p className="text-indigo-200">Manage your pets, appointments, and care schedules</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <SummaryCard 
            icon={PawPrint}
            title="My Pets"
            value={summary?.pets_count || 0}
            color="from-indigo-500 to-purple-500"
          />
          <SummaryCard 
            icon={CalendarDays}
            title="Upcoming Appointments"
            value={summary?.upcoming_appointments || 0}
            color="from-blue-500 to-cyan-500"
          />
          <SummaryCard 
            icon={Bell}
            title="Pending Reminders"
            value={summary?.pending_reminders || 0}
            subtitle={summary?.overdue_reminders > 0 ? `${summary.overdue_reminders} overdue` : null}
            color={summary?.overdue_reminders > 0 ? "from-red-500 to-orange-500" : "from-amber-500 to-yellow-500"}
          />
          <SummaryCard 
            icon={Syringe}
            title="Recent Vaccinations"
            value={summary?.recent_vaccinations || 0}
            subtitle="Last 30 days"
            color="from-green-500 to-emerald-500"
          />
        </div>
        
        {/* Main Content */}
        <Tabs defaultValue="pets" className="space-y-6">
          <TabsList className="bg-white shadow-sm">
            <TabsTrigger value="pets" className="flex items-center gap-2">
              <PawPrint className="w-4 h-4" />
              My Pets
            </TabsTrigger>
            <TabsTrigger value="appointments" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Appointments
            </TabsTrigger>
            <TabsTrigger value="reminders" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Reminders
              {reminders.filter(r => r.is_overdue).length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 justify-center">
                  {reminders.filter(r => r.is_overdue).length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          {/* Pets Tab */}
          <TabsContent value="pets">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">My Pets ({pets.length})</h2>
              <Button onClick={() => { setEditPet(null); setAddPetOpen(true); }} data-testid="add-pet-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Pet
              </Button>
            </div>
            
            {pets.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <PawPrint className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No pets yet</h3>
                  <p className="text-gray-500 mb-4">Add your first pet to start tracking their health</p>
                  <Button onClick={() => setAddPetOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Pet
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pets.map(pet => (
                  <PetCard 
                    key={pet.pet_id}
                    pet={pet}
                    onEdit={(p) => { setEditPet(p); setAddPetOpen(true); }}
                    onDelete={handleDeletePet}
                    onViewDetails={() => {}}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Upcoming Appointments</h2>
              <Button onClick={() => navigate('/pets')} variant="outline">
                Book New Appointment
              </Button>
            </div>
            
            {appointments.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No upcoming appointments</h3>
                  <p className="text-gray-500 mb-4">Book a service for your pet</p>
                  <Button onClick={() => navigate('/pets')}>
                    Browse Pet Services
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {appointments.map(apt => (
                  <AppointmentItem key={apt.booking_id} appointment={apt} />
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Reminders Tab */}
          <TabsContent value="reminders">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Care Reminders</h2>
              <Button onClick={() => setAddReminderOpen(true)} disabled={pets.length === 0} data-testid="add-reminder-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Reminder
              </Button>
            </div>
            
            {reminders.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Bell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No reminders set</h3>
                  <p className="text-gray-500 mb-4">Add reminders for vaccinations, grooming, and more</p>
                  <Button onClick={() => setAddReminderOpen(true)} disabled={pets.length === 0}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Reminder
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {reminders.filter(r => r.is_overdue).length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Overdue ({reminders.filter(r => r.is_overdue).length})
                    </h3>
                    {reminders.filter(r => r.is_overdue).map(reminder => (
                      <ReminderItem 
                        key={reminder.reminder_id}
                        reminder={reminder}
                        onComplete={handleCompleteReminder}
                        onDelete={handleDeleteReminder}
                      />
                    ))}
                  </div>
                )}
                
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Upcoming</h3>
                {reminders.filter(r => !r.is_overdue).map(reminder => (
                  <ReminderItem 
                    key={reminder.reminder_id}
                    reminder={reminder}
                    onComplete={handleCompleteReminder}
                    onDelete={handleDeleteReminder}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Dialogs */}
      <AddPetDialog 
        open={addPetOpen}
        onOpenChange={setAddPetOpen}
        onSave={handleSavePet}
        editPet={editPet}
      />
      
      <AddReminderDialog 
        open={addReminderOpen}
        onOpenChange={setAddReminderOpen}
        onSave={handleAddReminder}
        pets={pets}
      />
    </div>
  );
}
